import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL, SCHOOL_INFO } from "utils/constants";
import PanelHeader from "components/PanelHeader/PanelHeader.js";

const StudentCard = () => {
  const [student, setStudent] = useState(null);
  const [cardTemplate, setCardTemplate] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${BASE_URL}/student/getstudentcard.php`, { withCredentials: true })
      .then((response) => {
        if (!mounted) return;
        if (response.data?.status !== "success" || !response.data?.data) {
          setError(response.data?.msg || "Unable to load student card.");
          return;
        }
        setStudent(response.data.data.student);
        setCardTemplate(response.data.data.cardtemplate);
      })
      .catch((requestError) => {
        if (mounted) {
          setError(requestError.response?.data?.msg || "Unable to load student card.");
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (error) return <><PanelHeader size="sm" /><div className="content"><div className="alert alert-danger">{error}</div></div></>;
  if (!student || !cardTemplate) return <><PanelHeader size="sm" /><div className="content">Loading...</div></>;

  const placeholderValue = (key) => {
    switch (key) {
      case "studentName":
        return (student.fullname_english || student.fullname || "").split(" ").slice(0, 3).join(" ");
      case "studentId":
        return `${SCHOOL_INFO?.school_prefix || ""}${student.id || ""}`;
      case "studentImage":
        return student.driveid?.includes("https://") ? student.driveid : SCHOOL_INFO?.logo;
      case "qrCode":
        return `https://cloud.ivyis.org/qr/?size=300&text=${encodeURIComponent(student.qrcode || "")}&choe=UTF-8`;
      case "schoolInfo":
        return SCHOOL_INFO?.fullname || cardTemplate.school_name || "";
      case "fatherName":
        return `Father: ${student.father_name || ""}`;
      case "motherName":
        return `Mother: ${student.mother_name || ""}`;
      case "educationSystem":
        return `Education System: ${student.education_system || ""}`;
      case "grade":
        return `Grade Level: ${student.grade || ""}`;
      case "emergency_contact":
        return `Emergency Contact: ${student.emergency_contact || ""}`;
      default:
        return "";
    }
  };

  const renderPlaceholder = (placeholder, index) => {
    const value = placeholderValue(placeholder.key);
    if (placeholder.type === "text") {
      const baseSize = Number(placeholder.fontSize) || 14;
      const width = Number(placeholder.width) || 0;
      const requiredWidth = value.length * baseSize * 0.35;
      const scale = width && requiredWidth > width ? width / requiredWidth : 1;
      const fontSize = Math.max(8, baseSize * scale);
      const fontFamily = placeholder.fontFamily?.name
        ? `StudentCardFont_${placeholder.key}_${index}`
        : "inherit";

      return (
        <React.Fragment key={index}>
          {placeholder.fontFamily?.source && (
            <style>{`@font-face { font-family: '${fontFamily}'; src: url('${placeholder.fontFamily.source}') format('truetype'); }`}</style>
          )}
          <div style={{
            position: "absolute", left: placeholder.x, top: placeholder.y,
            color: placeholder.color || "#000", fontSize,
            fontWeight: placeholder.fontWeight || 400, fontFamily,
            width: placeholder.width || "auto", whiteSpace: "pre-wrap",
            wordBreak: "break-word", lineHeight: 1.1,
            textAlign: placeholder.textAlign || placeholder.align || "left",
          }}>{value}</div>
        </React.Fragment>
      );
    }
    if (placeholder.type === "image") {
      return <img key={index} src={value || placeholder.source} alt={placeholder.label || placeholder.key}
        style={{ position: "absolute", left: placeholder.x, top: placeholder.y,
          width: placeholder.width || "auto", height: placeholder.height || "auto",
          objectFit: "contain", aspectRatio: "1 / 1" }} />;
    }
    return null;
  };

  const width = Number(cardTemplate.card_width || cardTemplate.cardWidth) || 324;
  const height = Number(cardTemplate.card_height || cardTemplate.cardHeight) || 206;
  const transportationColor = student.transportation_status?.background_color || "#6c757d";
  const face = (side) => (
    <div style={{ position: "relative", flex: `0 0 ${width}px`, width, height,
      backgroundImage: `url(${cardTemplate[`${side}image`]})`, backgroundSize: "cover",
      backgroundPosition: "center", overflow: "hidden" }}>
      {cardTemplate[`${side}_placeholders`]?.map(renderPlaceholder)}
      {side === "front" && <div aria-label={`Transportation status: ${student.transportation_status?.english_label || "Not specified"}`} style={{
        position: "absolute", right: 0, bottom: 0, left: 0, height: 10,
        backgroundColor: transportationColor, zIndex: 20,
      }} />}
    </div>
  );

  return (
    <>
      <PanelHeader size="sm" />
      <div className="content" style={{ overflowX: "auto", paddingTop: 30, paddingBottom: 30 }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", justifyContent: "center", minWidth: Math.min(width, 320) }}>
          {cardTemplate.frontimage && face("front")}
          {cardTemplate.backimage && face("back")}
        </div>
      </div>
    </>
  );
};

export default StudentCard;
